import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useIsMounted, useStateInRef } from 'seagull';
import { useLogBIEvent } from '~/components/BiFrontendProvider/useLogBIEvent';
import { useIoContext, useOverlayContext, useStoreActions } from '~/contexts';
import { useSubscribeToConnectivitySignals } from '~/providers/ConnectivitySignalsProvider';
import { ConnectivityToast } from '../ConnectivityToast/ConnectivityToast';
import { applyRequestOutcome, checkAnchor, connectivityRestoredEvent, declareOutage, DEFAULT_CONNECTIVITY_STATE, isOutageSuspected, outageTransition, provesAnchorReached, startConfirmationChecks } from './useConnectivity.utils';
const IDENTITY_RELOAD_LOADER = 'connectivity-identity-reload';
export function useConnectivity(param) {
    let { retrySchedule, toastTestId } = param;
    const { getNow, browser } = useIoContext();
    const { api } = useStoreActions();
    const { showPersistentToast, startLoader, stopLoader } = useOverlayContext();
    const queryClient = useQueryClient();
    const logBIEvent = useLogBIEvent();
    const [announcement, setAnnouncement] = useState('idle');
    const session = useMemo(()=>({
            connectivity: DEFAULT_CONNECTIVITY_STATE,
            episode: null,
            identityReloadPending: false,
            stopConfirming: null
        }), []);
    const getOnSignal = useStateInRef(onSignal);
    const getOnOffline = useStateInRef(onOffline);
    const isMounted = useIsMounted();
    useSubscribeToConnectivitySignals((signal)=>getOnSignal()(signal));
    useEffect(()=>browser.registerOfflineListener(()=>getOnOffline()()), []);
    useEffect(()=>()=>{
            var _session_stopConfirming, _session_episode;
            if (session.identityReloadPending) stopLoader(IDENTITY_RELOAD_LOADER);
            session.identityReloadPending = false;
            (_session_stopConfirming = session.stopConfirming) === null || _session_stopConfirming === void 0 ? void 0 : _session_stopConfirming.call(session);
            (_session_episode = session.episode) === null || _session_episode === void 0 ? void 0 : _session_episode.dismiss();
        }, [
        session
    ]);
    function onSignal(signal) {
        if (signal.kind === 'requestOutcome') {
            record(signal.outcome);
        } else {
            if (!session.identityReloadPending) startLoader(IDENTITY_RELOAD_LOADER);
            session.identityReloadPending = true;
            void probe();
        }
    }
    function onOffline() {
        moveTo(declareOutage(session.connectivity, getNow().getTime()));
    }
    function record(outcome) {
        if (!isMounted()) return;
        const now = getNow().getTime();
        const identityCheckFailed = session.identityReloadPending && outcome === 'transportFailure';
        moveTo(identityCheckFailed ? declareOutage(session.connectivity, now) : applyRequestOutcome(session.connectivity, outcome, now));
        reloadIfIdentityPending(outcome);
    }
    function moveTo(next) {
        const transition = outageTransition(session.connectivity, next);
        session.connectivity = next;
        if ((transition === null || transition === void 0 ? void 0 : transition.kind) === 'lost') startEpisode(transition.since);
        if ((transition === null || transition === void 0 ? void 0 : transition.kind) === 'restored') endEpisode();
        confirmWhileSuspected();
    }
    function confirmWhileSuspected() {
        const suspected = isOutageSuspected(session.connectivity);
        if (suspected && !session.stopConfirming) {
            session.stopConfirming = startConfirmationChecks(retrySchedule.startTicker, ()=>void probe());
        }
        if (!suspected && session.stopConfirming) {
            session.stopConfirming();
            session.stopConfirming = null;
        }
    }
    function reloadIfIdentityPending(outcome) {
        if (session.identityReloadPending && provesAnchorReached(outcome)) {
            session.identityReloadPending = false;
            browser.locationReload();
        }
    }
    async function probe(trigger) {
        const outcome = await checkAnchor(api.connectivity.probeReachability);
        if (trigger && session.episode && provesAnchorReached(outcome)) {
            session.episode.trigger = trigger;
        }
        record(outcome);
    }
    function startEpisode(since) {
        const toast = /*#__PURE__*/ _jsx(ConnectivityToast, {
            testId: toastTestId,
            schedule: retrySchedule,
            probe: probe
        });
        const dismiss = showPersistentToast(toast, {
            layout: 'responsive',
            aboveGlobalLoader: true
        });
        session.episode = {
            since,
            trigger: 'request',
            dismiss
        };
        setAnnouncement('lost');
    }
    function endEpisode() {
        const ended = session.episode;
        if (!ended) return;
        session.episode = null;
        ended.dismiss();
        if (!session.identityReloadPending) void queryClient.refetchQueries({
            type: 'active'
        });
        logBIEvent(connectivityRestoredEvent(ended, getNow()));
        setAnnouncement('restored');
    }
    return announcement;
}
